import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { cn } from '@/shared/lib/cn';
import type { Segment, SegmentKind } from './segments';

// Nur Hintergrund und Unterstreichung: der Text selbst gehört dem Eingabefeld darüber.
const MARK: Record<Exclude<SegmentKind, 'text'>, string> = {
  field: 'bg-postmark/15',
  link: 'bg-navy/20',
  unknown: 'bg-sealwax/15 underline decoration-sealwax decoration-wavy',
  misplaced: 'bg-amber/30 underline decoration-amber decoration-wavy',
  stray: 'bg-sealwax/25',
};

const BOX =
  'w-full whitespace-pre-wrap break-words rounded-md border px-3 py-2 text-sm leading-relaxed';

interface Props {
  id: string;
  value: string;
  placeholder?: string;
  segments: Segment[];
  singleLine: boolean;
  maxLength: number;
  invalid: boolean;
  describedBy?: string;
  onChange: (value: string) => void;
  /** `{{` wurde gerade getippt; `caret` steht direkt dahinter. */
  onFieldTrigger: (caret: number) => void;
  /** Wechselt, wenn „Zum Feld“ genau dieses Feld meint; danach meldet das Feld den Sprung erledigt. */
  focusRequest?: number;
  onFocused?: () => void;
}

function fit(element: HTMLTextAreaElement) {
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
}

/**
 * Textfeld mit hervorgehobenen Feldern. Hinter dem Eingabefeld liegt eine deckungsgleiche Ebene,
 * die nur markiert; das Feld wächst mit seinem Inhalt, damit beide nie auseinanderscrollen.
 */
export const TokenField = forwardRef<HTMLTextAreaElement, Props>(function TokenField(props, ref) {
  const inner = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => inner.current!);

  useLayoutEffect(() => {
    if (inner.current) fit(inner.current);
  }, [props.value]);

  // Verborgen (schmale Vorschau-Ansicht) misst das Feld 0 px; sichtbar geworden, misst es neu.
  useEffect(() => {
    const element = inner.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let width = element.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (element.offsetWidth === width) return;
      width = element.offsetWidth;
      fit(element);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const { focusRequest = 0, onFocused } = props;
  useEffect(() => {
    if (focusRequest === 0) return;
    inner.current?.focus();
    onFocused?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur ein neuer Sprung fokussiert
  }, [focusRequest]);

  return (
    <div className="relative">
      <div aria-hidden className={cn(BOX, 'absolute inset-0 border-transparent text-transparent')}>
        {props.segments.map((segment, index) =>
          segment.kind === 'text' ? (
            <span key={index}>{segment.text}</span>
          ) : (
            <mark key={index} className={cn('rounded-sm text-transparent', MARK[segment.kind])}>
              {segment.text}
            </mark>
          ),
        )}
        {/* Ein Umbruch am Ende hält die Ebene so hoch wie das Feld mit seiner letzten Zeile. */}
        {'\n'}
      </div>
      <textarea
        ref={inner}
        id={props.id}
        value={props.value}
        placeholder={props.placeholder}
        rows={props.singleLine ? 1 : 6}
        maxLength={props.maxLength}
        aria-invalid={props.invalid || undefined}
        aria-describedby={props.describedBy}
        onKeyDown={(event) => {
          if (props.singleLine && event.key === 'Enter') event.preventDefault();
        }}
        onChange={(event) => {
          const next = props.singleLine
            ? event.target.value.replace(/\r?\n/g, ' ')
            : event.target.value;
          props.onChange(next);
          const caret = event.target.selectionStart ?? next.length;
          if (next.length === props.value.length + 1 && next.slice(0, caret).endsWith('{{')) {
            props.onFieldTrigger(caret);
          }
        }}
        className={cn(
          BOX,
          'text-ink caret-ink placeholder:text-ink-faint relative block resize-none overflow-hidden bg-transparent outline-none',
          'focus:border-sage focus:ring-sage/20 focus:ring-2',
          props.invalid ? 'border-sealwax/60' : 'border-line',
        )}
      />
    </div>
  );
});
